"use client";

// src/card/index.tsx
import * as React2 from "react";
import {
  Base,
  systemForwardRef,
  useStyleConfig,
  mergeStyles
} from "@stepstone-genesis/system";
import { useCardGroupContext } from "@stepstone-genesis/components-card-group";
import { Disclosure } from "@stepstone-genesis/components-disclosure";

// src/context.tsx
import * as React from "react";
var CardContext = React.createContext({
  isCollapsible: false,
  variant: "default",
  withMultipleContent: false
});
var useCardContext = () => {
  const context = React.useContext(CardContext);
  if (!context) {
    throw new Error("useCardContext must be used within CardContextProvider");
  }
  return context;
};
CardContext.displayName = "CardContext";

// src/card/styles.ts
var root = ({
  hasBorders,
  isActionable,
  isSubdued
}) => ({
  color: isSubdued ? "colorBorderSubdued" : "colorBorderDefault",
  borderRadius: "radiusM",
  backgroundColor: "colorWhite",
  boxShadow: hasBorders ? "boxShadowBorderDefault" : "boxShadowNone",
  //to fit content (like images) to rounded corners
  overflow: "hidden",
  // fix problem for children with position absolute
  position: "inherit",
  ...isActionable ? {
    transition: "ease-out 0.13s",
    _focusVisible: {
      outline: "outlineDefault",
      outlineOffset: "2px",
      boxShadow: "boxShadowFocusWhite"
    },
    _hover: {
      cursor: "pointer",
      outline: "outlineDefault",
      outlineColor: "colorActionableHover"
    },
    _active: {
      outline: "outlineDefault",
      outlineColor: "colorActionableActive"
    }
  } : {}
});
var styles = {
  root
};

// src/card/index.tsx
var Card = systemForwardRef(function Card2({
  isCollapsible = false,
  isVisible = true,
  variant = "default",
  isActionable,
  children,
  withMultipleContent = false,
  __disable_card_group_ctx = false,
  hasBorders = true,
  isFullWidth = false,
  isSubdued = false,
  ...rest
}, ref) {
  const { groupRootStyles, isFullWidth: isFullWidthCard } = useCardGroupContext();
  const shouldBeActionable = !!groupRootStyles && isActionable && !isCollapsible;
  const { CARD } = useStyleConfig("CARD", {
    styles: {
      base: styles.root({
        hasBorders,
        isActionable: shouldBeActionable,
        isSubdued
      }),
      responsiveVariants: [
        {
          id: "card-full-width",
          prop: isFullWidth || isFullWidthCard,
          variants: {
            true: {
              borderRadius: "radiusNone",
              boxShadow: "boxShadowBorderHorizontal"
            },
            false: {
              borderRadius: "radiusM",
              boxShadow: "boxShadowBorderDefault"
            }
          }
        }
      ]
    },
    props: {
      variant
    }
  });
  return /* @__PURE__ */ React2.createElement(
    CardContext.Provider,
    {
      value: {
        isCollapsible,
        variant,
        withMultipleContent,
        hasBorders,
        isSubdued
      }
    },
    /* @__PURE__ */ React2.createElement(
      Base,
      {
        ref,
        as: "article",
        __root: mergeStyles([
          CARD.styles,
          __disable_card_group_ctx ? void 0 : groupRootStyles
        ]),
        element: "CARD",
        role: shouldBeActionable && "button",
        tabIndex: shouldBeActionable && 0,
        ...rest
      },
      isCollapsible && children ? /* @__PURE__ */ React2.createElement(Disclosure, { isVisible, children, isAnimatable: true }) : children
    )
  );
});

// src/card-header/index.tsx
import * as React3 from "react";
import {
  Base as Base2,
  mergeStyles as mergeStyles2,
  systemForwardRef as systemForwardRef2,
  useStyleConfig as useStyleConfig2,
  useTheme
} from "@stepstone-genesis/system";
import {
  DisclosureTrigger,
  DisclosureIndicator
} from "@stepstone-genesis/components-disclosure";
import { Heading } from "@stepstone-genesis/components-heading";
import {
  MediaBody,
  MediaFigure,
  MediaObject
} from "@stepstone-genesis/components-media-object-layout";

// src/card-header/styles.ts
var root2 = (theme, hasBorders, isSubdued) => {
  var _a, _b;
  return {
    borderRadius: "radiusM",
    backgroundColor: "colorWhite",
    boxShadow: hasBorders ? "boxShadowBorderDefault" : "boxShadowNone",
    color: isSubdued ? "colorBorderSubdued" : "colorBorderDefault",
    _expanded: {
      borderBottomLeftRadius: "radiusNone",
      borderBottomRightRadius: "radiusNone",
      cursor: "pointer",
      _focusVisible: {
        // Inset is used because we use overflow hidden and it cuts the box-shadow
        boxShadow: `inset 0 0 0 2px ${(_a = theme.colors) == null ? void 0 : _a.colorBlack}`
      }
    },
    _notExpanded: {
      borderBottomLeftRadius: "radiusM",
      borderBottomRightRadius: "radiusM",
      cursor: "pointer",
      _focusVisible: {
        boxShadow: `inset 0 0 0 2px ${(_b = theme.colors) == null ? void 0 : _b.colorBlack}`
      }
    }
  };
};
var headerVariants = {
  default: {
    paddingX: ["spaceM", "spaceL"],
    paddingY: "spaceS"
  },
  compact: {
    paddingX: "spaceM",
    paddingY: "spaceS"
  }
};
var headingVariants = {
  default: {
    fontSize: ["fontSizeS", "fontSizeM"],
    lineHeight: ["lineHeightS", "lineHeightM"]
  },
  compact: {
    fontSize: "fontSizeS",
    lineHeight: "lineHeightS"
  }
};
var styles2 = { root: root2, headerVariants, headingVariants };

// src/card-header/index.tsx
var ChildrenContainer = systemForwardRef2(
  ({ children, variant, ...rest }, ref) => {
    const cardContext = useCardContext();
    const cardHeaderVariant = variant != null ? variant : cardContext.variant;
    const { CARD_HEADING } = useStyleConfig2("CARD_HEADING", {
      styles: {
        variants: styles2.headingVariants
      },
      props: {
        variant: cardHeaderVariant
      }
    });
    return /* @__PURE__ */ React3.createElement(Base2, { as: "div", ref, variant, ...rest }, typeof children === "string" ? /* @__PURE__ */ React3.createElement(
      Heading,
      {
        variant: "heading4",
        element: "CARD_HEADING",
        sx: CARD_HEADING.styles
      },
      children
    ) : children);
  }
);
var CardHeader = systemForwardRef2(
  ({ children, variant, ...rest }, ref) => {
    const cardContext = useCardContext();
    const cardHeaderVariant = variant != null ? variant : cardContext.variant;
    const isCollapsible = cardContext.isCollapsible;
    const { theme } = useTheme();
    const { CARD_HEADER_CONTAINER } = useStyleConfig2("CARD_HEADER_CONTAINER", {
      styles: {
        base: styles2.root(theme, cardContext.hasBorders, cardContext.isSubdued),
        variants: styles2.headerVariants
      },
      props: {
        variant: cardHeaderVariant
      }
    });
    return isCollapsible ? /* @__PURE__ */ React3.createElement(DisclosureTrigger, null, /* @__PURE__ */ React3.createElement(
      MediaObject,
      {
        sx: mergeStyles2([CARD_HEADER_CONTAINER.styles]),
        element: "CARD_HEADER_CONTAINER",
        alignX: "center"
      },
      /* @__PURE__ */ React3.createElement(MediaBody, null, /* @__PURE__ */ React3.createElement(
        ChildrenContainer,
        {
          ref,
          variant: cardHeaderVariant,
          children,
          ...rest
        }
      )),
      /* @__PURE__ */ React3.createElement(MediaFigure, null, /* @__PURE__ */ React3.createElement(DisclosureIndicator, { color: "colorGreyDark" }))
    )) : /* @__PURE__ */ React3.createElement(
      MediaObject,
      {
        sx: CARD_HEADER_CONTAINER.styles,
        element: "CARD_HEADER_CONTAINER",
        alignX: "center"
      },
      /* @__PURE__ */ React3.createElement(MediaBody, null, /* @__PURE__ */ React3.createElement(
        ChildrenContainer,
        {
          ref,
          variant: cardHeaderVariant,
          children,
          ...rest
        }
      ))
    );
  }
);

// src/card-content/index.tsx
import * as React4 from "react";
import {
  Base as Base3,
  systemForwardRef as systemForwardRef3,
  useStyleConfig as useStyleConfig3
} from "@stepstone-genesis/system";
import { DisclosureContent } from "@stepstone-genesis/components-disclosure";

// src/card-content/styles.ts
var root3 = {};
var variants = {
  default: {
    padding: ["spaceM", "spaceL"]
  },
  compact: {
    padding: ["spaceM"]
  },
  reset: {
    padding: ["spaceNone"]
  }
};
var multipleContent = {
  boxShadow: "0 -2px 1px -1px"
};
var styles3 = { root: root3, variants, multipleContent };

// src/card-content/index.tsx
var Content = systemForwardRef3(({ children, variant, multipleContentStyles, ...rest }, ref) => {
  const { CARD_CONTENT } = useStyleConfig3("CARD_CONTENT", {
    styles: {
      base: {
        ...styles3.root,
        ...multipleContentStyles
      },
      variants: styles3.variants
    },
    props: { variant }
  });
  return /* @__PURE__ */ React4.createElement(
    Base3,
    {
      ref,
      as: "div",
      variant,
      element: "CARD_CONTENT",
      __root: CARD_CONTENT.styles,
      ...rest
    },
    children
  );
});
var CardContent = systemForwardRef3(
  ({ children, variant, ...rest }, ref) => {
    const cardContext = useCardContext();
    const cardContentVariant = variant != null ? variant : cardContext.variant;
    const isCollapsible = cardContext.isCollapsible;
    const multipleContentStyles = cardContext.withMultipleContent ? styles3.multipleContent : {};
    return isCollapsible ? /* @__PURE__ */ React4.createElement(
      DisclosureContent,
      {
        sx: {
          ...multipleContentStyles,
          display: "inherit"
        }
      },
      /* @__PURE__ */ React4.createElement(
        Content,
        {
          ref,
          variant: cardContentVariant,
          children,
          ...rest
        }
      )
    ) : /* @__PURE__ */ React4.createElement(
      Content,
      {
        ref,
        variant: cardContentVariant,
        children,
        multipleContentStyles,
        ...rest
      }
    );
  }
);
export {
  Card,
  CardContent,
  CardHeader
};
